'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Popconfirm, Space, type TableProps } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useConnection, useUser } from '@sezenta/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import CollectionReportViewModal, {
  useCollectionReportViewModal,
} from './CollectionReportViewModal';
import CollectionReportEditModal, {
  useCollectionReportEditModal,
} from './collection-report-edit-modal';
import { useEventListener } from '../../common/EventContext';
import {
  FilterFields,
  FilterPanel,
  FilterValue,
  FilterValues,
} from '../../common/antd-filter-panel';
import CTable from '../../components/shared-ui/CTable';
import { Collection, Collections, Permission } from '../../dto';
import { Utils } from '../../common/Utils';
import { TableInfoPanel } from '../../common/TableInfoPanel';
import CollectionFormModal, {
  useCollectionFormModal,
} from '../../common/CollectionFormModal';

const CollectionReport = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const connection = useConnection();
  const user = useUser();
  const userInfo = useUser();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [summary, setSummary] = useState<{ [key: string]: string | number }>(
    {},
  );
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextStartKey, setNextStartKey] = useState<number>();
  const viewModal = useCollectionReportViewModal();
  const editModal = useCollectionReportEditModal();
  const collectionFormModal = useCollectionFormModal();
  const { sites, businessId, select, collectionType } = useBusinessContext();
  // Creator options for the "Created User" filter. Fetched from the report itself
  // (distinct actual creators) rather than a full user directory -- the context
  // user list was server-paginated and silently truncated to its first page.
  const [creatorOptions, setCreatorOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [value, setValue] = useState<FilterValues>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [filter, setFilter] = useState<FilterValues>();
  const fetchData = useCallback(
    (startKey?: number, filters?: FilterValues) => {
      setLoading(true);
      connection
        .get(`collection/all/${businessId}`, { params: { startKey, filters } })
        .then((res) => {
          setSummary(res.data.summary);
          if (startKey) {
            setCollections((prevCollections) => [
              ...prevCollections,
              ...res.data.collections,
            ]);
          } else {
            setCollections(res.data.collections);
          }
          setHasMore(res.data.hasMore);
          setNextStartKey(res.data.nextStartKey);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setLoading(false);
          setFilter(filters);
        });
    },
    [businessId, connection],
  );

  useEffect(() => {
    let cancelled = false;
    connection
      .get(`collection/${businessId}/creators`)
      .then((res) => {
        if (cancelled) return;
        setCreatorOptions(
          (res.data ?? []).map((c: { userId: string; name: string }) => ({
            label: c.name,
            value: c.userId,
          })),
        );
      })
      .catch((error) => {
        // A failed lookup only costs the filter its options; the report itself
        // still renders, so don't surface it as a page-level error.
        console.log(error);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, connection]);

  const handleSummeryUpdate = useCallback(() => {
    connection
      .get(`collection/get-report-summery/${businessId}`, {
        params: { filter },
      })
      .then((res) => setSummary(res.data));
  }, [businessId, connection, filter]);

  useEffect(() => {
    console.log('AA', userInfo);
    fetchData();
  }, [fetchData, userInfo]);

  useEventListener('COLLECTION', (evt) => {
    if (evt.action === 'UPDATE') {
      setCollections((prevCollections) =>
        prevCollections.map((c) => (c.id === evt.data.id ? evt.data : c)),
      );
      // fetchData(undefined, filter);
      handleSummeryUpdate();
    } else if (evt.action === 'CREATE') {
      setCollections((prevCollections) => [evt.data, ...prevCollections]);
      // fetchData(undefined, filter);
      handleSummeryUpdate();
    } else {
      setCollections((prevCollections) =>
        prevCollections.filter((c) => c.id !== evt.data.id),
      );
    }
  });

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        setLoading(true);
        await connection.delete(`collection/${businessId}/${id}`);
        setCollections((prev) => prev.filter((c) => c.id !== id));
        handleSummeryUpdate();
        message.success('Collection deleted successfully');
      } catch (error) {
        console.error('Delete failed:', error);
        message.error('Failed to delete collection');
      } finally {
        setLoading(false);
      }
    },
    [businessId, connection, handleSummeryUpdate],
  );

  const columns = useMemo(() => {
    const temp: TableProps<Collection>['columns'] = [
      {
        title: 'Collection Date & Time',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (timestamp) =>
          Utils.toDayJs(new Date(parseInt(timestamp))).format(
            'D of MMM, YYYY - h:mmA',
          ),
      },
      {
        title: 'Store Name',
        dataIndex: 'siteId',
        key: 'siteId',
        render: (_, record) => {
          return (
            <span
              onClick={() => {
                viewModal.current?.view(record);
              }}
              className={'text-teal-500 cursor-pointer'}
            >
              {sites[record.siteId]?.name}
            </span>
          );
        },
      },
      {
        title: 'Cost Center',
        dataIndex: 'costCenter',
        key: 'costCenter',
        render: (_, record) => {
          return <span>{sites[record.siteId]?.costCenter}</span>;
        },
      },
      {
        title: 'Store Address',
        dataIndex: 'siteId',
        key: 'siteId',
        render: (_, record) => {
          return <span>{sites[record.siteId]?.address}</span>;
        },
      },
      // {
      //   title: 'Area',
      //   dataIndex: 'area',
      //   key: 'area',
      //   render: (_, record) => {
      //     return <span>{sites[record.siteId]?.area}</span>;
      //   },
      // },
      {
        title: 'Suburb',
        dataIndex: 'suburb',
        key: 'suburb',
        render: (_, record) => {
          return <span>{sites[record.siteId]?.suburb}</span>;
        },
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        render: (_, record) => {
          return <span>{sites[record.siteId]?.state}</span>;
        },
      },
      {
        title: 'Council',
        dataIndex: 'council',
        key: 'council',
        render: (_, record) => {
          return <span>{sites[record.siteId]?.council}</span>;
        },
      },
      // {
      //   title: 'Zone',
      //   dataIndex: 'zone',
      //   key: 'zone',
      //   render: (_, record) => {
      //     return <span>{sites[record.siteId]?.zone}</span>;
      //   },
      // },
      {
        title: 'Created User',
        dataIndex: 'createdBy',
        key: 'createdBy',
        render: (_, record) => (
          <span>{record.createdByName ?? 'Unknown User'}</span>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'isAdditionalCollection',
        key: 'status',
        render: (isAdditional) =>
          isAdditional ? (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
              Additional Collection
            </span>
          ) : (
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
              Regular Collection
            </span>
          ),
      },
      {
        title: 'Collection',
        dataIndex: 'collections',
        key: 'collections',
        render: (collections) =>
          collections.map((item: Collections) => (
            <div
              key={item.collectionType}
              className="flex gap-4 text-xs font-semibold"
            >
              <span className="text-primary min-w-8">{`${item.quantity}x`}</span>
              <span className="min-w-12">
                {`${collectionType[item.collectionType]?.name || 'N/A'}`}
              </span>
              <span className="text-primary-50 min-w-12">
                {item.weight || 0} kg
              </span>
            </div>
          )),
      },
      {
        title: 'Total Weight',
        dataIndex: 'collections',
        key: 'weight',
        render: (collections: Collections[]) => {
          const totalWeight = collections.reduce((acc: number, item) => {
            return acc + item.weight;
          }, 0);
          return `${totalWeight} kg`;
        },
      },
    ];

    if (
      user?.permissions.includes(
        Permission.SUBMIT_REPORT || Permission.DELETE_REPORT,
      )
    ) {
      temp.push({
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            {user?.permissions.includes(Permission.EDIT_REPORT) && (
              <>
                <Button
                  title="Edit Report"
                  icon={<EditOutlined />}
                  type="text"
                  className="text-[20px] text-gray-500"
                  onClick={() => editModal.current?.edit(record)}
                />
                {user?.permissions.includes(Permission.DELETE_REPORT) && (
                  <Popconfirm
                    okButtonProps={{ loading }}
                    onConfirm={() => handleDelete(record.id)}
                    placement="topLeft"
                    title="Are you sure you want to delete this collection?"
                  >
                    <Button
                      title="Delete Report"
                      icon={<DeleteOutlined />}
                      type="text"
                      className="text-[20px] text-red-500"
                    />
                  </Popconfirm>
                )}
              </>
            )}
          </Space>
        ),
      });
    }

    return temp;
  }, [
    collectionType,
    editModal,
    handleDelete,
    loading,
    sites,
    user?.permissions,
    viewModal,
  ]);

  const selectedStateFilter = useMemo(() => {
    const stateValue = value.find((v) => v.name === 'state')?.value;
    if (Array.isArray(stateValue)) {
      return stateValue[0];
    }
    return stateValue as string | number | undefined;
  }, [value]);

  const getFilteredCollectionTypeOptions = useCallback(
    (stateValue?: string | number) => {
      if (!stateValue) {
        return [];
      }
      return Object.values(collectionType)
        .filter((c) => `${c?.state}` === `${stateValue}`)
        .filter((c) => c.status === 'Active')
        .map((c) => ({
          label: c.name,
          value: c.id,
          name: c.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    [collectionType],
  );

  const filteredCollectionTypeOptions = useMemo(
    () => getFilteredCollectionTypeOptions(selectedStateFilter),
    [getFilteredCollectionTypeOptions, selectedStateFilter],
  );

  const filterSchema: FilterFields = useMemo(() => {
    const baseSchema: FilterFields = [
      {
        label: 'Store',
        name: 'site',
        operators: ['eq'],
        type: 'select',
        options: select['sites'],
        masterDataType: 'site',
      },
      {
        label: 'Suburb',
        name: 'suburb',
        operators: ['eq'],
        type: 'select',
        options: select['suburbs'],
        masterDataType: 'suburbs',
      },
      {
        label: 'Zone',
        name: 'zone',
        operators: ['eq'],
        type: 'select',
        options: select['zones'],
        masterDataType: 'zone',
      },
      {
        label: 'State',
        name: 'state',
        operators: ['eq'],
        type: 'select',
        options: select['states'],
        masterDataType: 'states',
      },
      {
        label: 'Collection Type',
        name: 'collectionType',
        operators: ['eq'],
        type: 'select',
        options: filteredCollectionTypeOptions,
        masterDataType: 'collectionType',
        disabled: !selectedStateFilter,
        disabledReason: 'Select a state to filter by collection',
      },
      {
        label: 'Date',
        name: 'createdAt',
        operators: ['eq', 'between'],
        type: 'date',
      },
      {
        label: 'Created User',
        name: 'createdBy',
        operators: ['eq'],
        type: 'select',
        options: creatorOptions,
      },
      {
        label: 'Status',
        name: 'isAdditionalCollection',
        operators: ['eq'],
        type: 'radio',
        options: [
          { value: 'Additional Collection', label: 'Additional Collection' },
          { value: 'Regular Collection', label: 'Regular Collection' },
        ],
      },
    ];

    return baseSchema;
  }, [
    filteredCollectionTypeOptions,
    selectedStateFilter,
    select,
    creatorOptions,
  ]);

  const handleFilterChange = useCallback(
    (filters?: FilterValues) => {
      const updatedFilters = [...(filters ?? [])];

      const stateFilterIndex = updatedFilters.findIndex(
        (f) => f.name === 'state',
      );
      const collectionTypeFilter = updatedFilters.find(
        (f) => f.name === 'collectionType',
      );

      const stateFilterValue = updatedFilters.find(
        (f) => f.name === 'state',
      )?.value;

      let nextState: string | number | undefined = Array.isArray(
        stateFilterValue,
      )
        ? stateFilterValue[0]
        : (stateFilterValue as string | number | undefined);

      // Ensure a state filter is present when collection type is selected
      if (!nextState && collectionTypeFilter) {
        const collectionTypeValue = Array.isArray(collectionTypeFilter.value)
          ? collectionTypeFilter.value[0]
          : collectionTypeFilter.value;
        const inferredState =
          collectionTypeValue !== undefined
            ? collectionType[`${collectionTypeValue}`]?.state
            : undefined;
        if (inferredState !== undefined) {
          nextState = inferredState;
          const derivedStateFilter: FilterValue = {
            name: 'state',
            operator: 'eq',
            value: inferredState,
          };

          if (stateFilterIndex >= 0) {
            updatedFilters[stateFilterIndex] = derivedStateFilter;
          } else {
            updatedFilters.push(derivedStateFilter);
          }
        }
      }

      const allowedCollectionTypes =
        getFilteredCollectionTypeOptions(nextState);

      const sanitizedFilters = updatedFilters.filter((f) => {
        if (f.name !== 'collectionType') {
          return true;
        }
        if (!nextState) {
          return false;
        }
        const collectionTypeValue = Array.isArray(f.value)
          ? f.value[0]
          : f.value;
        return allowedCollectionTypes.some(
          (option) => `${option.value}` === `${collectionTypeValue}`,
        );
      });

      const result = sanitizedFilters.length > 0 ? sanitizedFilters : undefined;
      setValue(result ?? []);
      fetchData(undefined, result);
      console.log(JSON.stringify(result, null, 2));
    },
    [collectionType, fetchData, getFilteredCollectionTypeOptions],
  );
  const handlerExport = useCallback(() => {
    setIsExporting(true);
    connection
      .get(`collection/${businessId}/export-csv`, {
        params: { filters: value },
      })
      .then((res) => {
        if (res.data.url) {
          message.success('Export started successfully');
          window.open(res.data.url, '_blank');
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsExporting(false);
      });
  }, [businessId, connection, value]);

  return (
    <div className="w-full h-full">
      <div className="flex flex-col p-5 ctable">
        {/*<div className="w-full pl-3 flex justify-start mb-3">*/}
        {/*  <Breadcrumb separator=">" items={items} />*/}
        {/*</div>*/}
        <div className="flex pl-3 pb-4">
          <div className="flex gap-8 items-center mr-auto">
            <div className="text-[24px] font-[600]">Collection Report</div>
          </div>
        </div>
        <CTable
          loading={loading}
          title="Collections"
          columns={columns}
          data={collections}
          handledScroll={true}
          hideSearch
          pagination={false}
          hasMore={hasMore}
          loadMore={() => {
            fetchData(nextStartKey, value);
          }}
          rowClassName={(record) =>
            record.isAdditionalCollection
              ? 'bg-orange-50 hover:bg-orange-100'
              : ''
          }
          filterComponent={
            <FilterPanel
              fields={filterSchema}
              value={value}
              onChange={handleFilterChange}
            />
          }
          infoComponent={<TableInfoPanel data={summary} />}
          // headerRight={
          //   <Button
          //     loading={isExporting}
          //     disabled={isExporting}
          //     onClick={handlerExport}
          //     type="primary"
          //   >
          //     Export
          //   </Button>
          // }
          headerRight={
            <div className="flex gap-3">
              {user?.permissions.includes(Permission.SUBMIT_REPORT) && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => collectionFormModal.current?.open()}
                >
                  Add Collection
                </Button>
              )}
              {user?.permissions.includes(Permission.EXPORT_COLLECTION) ? (
                <Button
                  loading={isExporting}
                  disabled={isExporting}
                  onClick={handlerExport}
                  type="primary"
                >
                  Export
                </Button>
              ) : (
                <div className={'w-[1px] h-[25px]'} />
              )}
            </div>
          }
        />
      </div>
      <CollectionReportViewModal ref={viewModal} />
      <CollectionReportEditModal ref={editModal} />
      <CollectionFormModal ref={collectionFormModal} />
    </div>
  );
};

export default CollectionReport;
